/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { useQuery } from '../../../../Hooks/API/useQuery';
import { useLazyQuery } from '../../../../Hooks/API/useLazyQuery';
import { useQueryParamsStore } from '../../../../Hooks/Zustand/useQueryParamsStore';
import { useCheckoutStore } from '../../../../Hooks/Zustand/useCheckoutStore';
import { useDataAttributesStore } from '../../../../Hooks/Zustand/useDataAttributeStore';

/**
 * Custom ContractForm hook.
 * @returns {{isLoading, rateBundleData, findBundleForTerm, isPartOfTheSelectedContract}}
 */
export function useContractForm() {
  const {
    values: {
      rateBundleTerm,
      rateBundle,
      studioId,
      studio,
    },
    setFieldValue,
  } = useFormikContext();

  const {
    getQueryParameters,
    getQueryParameter,
  } = useQueryParamsStore();
  const {
    getDataAttribute,
  } = useDataAttributesStore();
  const {
    updateFormDataValue,
    setLastValidStep,
    getActiveStep,
    getFormDataValue,
  } = useCheckoutStore();

  /**
   * Check if the given rate bundle data contains a term which has the given default duration.
   * @param defaultDuration
   * @param rateBundleData
   * @returns {boolean}
   */
  const defaultDurationIsValid = (defaultDuration, rateBundleData) => {
    let isValid = false;
    rateBundleData.forEach((contractRateBundle) => {
      contractRateBundle.terms.forEach((term) => {
        if (term.termValue === defaultDuration) {
          isValid = true;
        }
      });
    });
    return isValid;
  };

  /**
   * Get the term duration
   *
   * Priority 1: Get from url
   * Priority 2: Get from data attribute
   * Priority 3: Fallback (12)
   *
   * @returns {number}
   */
  const getTermDuration = useCallback((rateBundleData) => {
    if (rateBundleData) {
      // Priority 1:
      if (getQueryParameter('defaultDuration')) {
        const defaultDuration = parseInt(getQueryParameter('defaultDuration'), 10);
        if (defaultDurationIsValid(defaultDuration, rateBundleData)) {
          return defaultDuration;
        }
      }
      // Priority 2
      if (getDataAttribute('defaultDuration')) {
        const defaultDuration = parseInt(getDataAttribute('defaultDuration'), 10);
        if (defaultDurationIsValid(defaultDuration, rateBundleData)) {
          return defaultDuration;
        }
      }
    }
    // Priority 3
    return 12;
  }, [getDataAttribute, getQueryParameter]);

  const studioDetailsInitialized = useRef(false);
  const {
    lazyQuery,
    data: studioData,
  } = useLazyQuery('studioDetails', null, { studioId: getQueryParameters().studioId });

  /**
   * Initialize lazyQuery when url contains studioId parameter
   */
  useEffect(() => {
    if (!studioDetailsInitialized.current && !studio) {
      studioDetailsInitialized.current = true;
      if (getQueryParameters().studioId) {
        lazyQuery({});
      }
    }
  }, [getQueryParameters, lazyQuery, studio]);

  /**
   * Set studio data to formik context
   */
  useEffect(() => {
    if (studioData && !studio) {
      updateFormDataValue('studioId', studioData.id);
      setFieldValue('studioId', studioData.id);
      updateFormDataValue('studio', studioData);
      setFieldValue('studio', studioData);
    }
  }, [setFieldValue, studio, studioData, updateFormDataValue]);

  const {
    data,
    isLoading,
  } = useQuery('rateBundle', { studioId: studioId || getQueryParameters().studioId });

  // State for term selection and contract selection
  const [termSelection, setTermSelection] = useState(12);
  const [contractSelection, setContractSelection] = useState(rateBundle || null);
  const dataInitialized = useRef(false);
  const componentInitialized = useRef(false);

  /**
   * Set term selection only once when data is initialized
   */
  useEffect(() => {
    if (data && !dataInitialized.current) {
      setTermSelection(rateBundleTerm ? rateBundleTerm.termValue : getTermDuration(data));
      dataInitialized.current = true;
    }
  }, [data, getTermDuration, rateBundleTerm]);

  /**
   * Check if a promotion contract is currently active.
   * @param {Object} possibleContract - The potential promotion contract.
   * @returns {boolean} - Whether the promotion is active.
   */
  const contractIsActive = useCallback((possibleContract) => {
    // when not given the contract is active
    if (!possibleContract.limitedOfferingPeriod) return true;

    const formatDate = (date) => dayjs(date)
      .format('YYYY-MM-DD');
    const currentDate = dayjs();
    const formattedCurrentDate = formatDate(currentDate);

    // Check if currentDate is between or equal to the start and end dates
    const {
      startDate,
      endDate,
    } = possibleContract.limitedOfferingPeriod;
    return formattedCurrentDate >= startDate && formattedCurrentDate <= endDate;
  }, []);

  /**
   * Merge promotion contracts into standard contracts.
   * @param {Array} standardContracts - Array of standard contracts.
   * @param {Array} possiblePromotionContracts - Array of potential promotion contracts.
   * @returns {Array} - Merged array of contracts with promotions.
   */
  const mergePromotionContractsIntoStandardContracts = useCallback((standardContracts, possiblePromotionContracts) => {
    const contractsWithPromotions = standardContracts;
    possiblePromotionContracts.forEach((possibleContract) => {
      // Test if the promotionContract has a related standardContract
      standardContracts.find((standardContract, key) => {
        // When the possibleContract is part of a standardContract,
        // it is an actionContract when any of the contract.rateCodeDto[].name prop matches with any dataArray[].name prop
        if (possibleContract.rateCodeDto.some((rateCode) => rateCode.name.toLowerCase() === standardContract.name.toLowerCase())) {
          if (!contractsWithPromotions[key] || !contractsWithPromotions[key].promotions) {
            contractsWithPromotions[key] = {
              ...standardContracts[key],
              promotions: [],
            };
          }
          // TermValue used as key to easier access the promotion contract
          possibleContract.terms.forEach((termContract) => {
            contractsWithPromotions[key].promotions[termContract.termValue] = possibleContract;
          });
        }
        return false;
      });
    });
    return contractsWithPromotions;
  }, []);

  /**
   * Build an array of standard contracts.
   * @param {Array} contractsData - Array of contracts data.
   * @returns {Array} - Array of standard contracts.
   */
  const buildStandardContracts = useCallback((contractsData) => {
    const possiblePromotionContracts = [];
    const contracts = [];

    if (!contractsData) return contracts;

    contractsData.forEach((contract) => {
      // Skip this contract because it's not active yet or anymore
      if (!contractIsActive(contract)) return;

      const isStandardContract = contract.rateCodeDto.some((rateCode) => (rateCode.name.toLowerCase() === contract.name.toLowerCase()));

      if (isStandardContract) {
        contracts.push(contract);
      } else {
        possiblePromotionContracts.push(contract);
      }
    });

    if (possiblePromotionContracts.length && contracts.length) {
      return mergePromotionContractsIntoStandardContracts(contracts, possiblePromotionContracts);
    }

    return contracts;
  }, [contractIsActive, mergePromotionContractsIntoStandardContracts]);

  const mergedContractsWithPromotions = useMemo(() => buildStandardContracts(data), [data, buildStandardContracts]);

  /**
   * Get available terms from rateBundleData.
   * @returns {Array} - Array of available terms.
   */
  const getAvailableTerms = () => [...new Set(mergedContractsWithPromotions.flatMap(
    (obj) => obj.terms.map((term) => term.termValue),
  ))].sort((a, b) => a - b);

  /**
   * Handle contract selection.
   * @param {Object} dataFromSelection - Data from the selected contract.
   */
  const handleContractSelection = useCallback((dataFromSelection) => {
    setContractSelection(dataFromSelection.bundle);
    setFieldValue('rateBundleTerm', dataFromSelection.term);
    updateFormDataValue('rateBundleTerm', dataFromSelection.term);
    setFieldValue('rateBundle', dataFromSelection.bundle);
    updateFormDataValue('rateBundle', dataFromSelection.bundle);
    setFieldValue('originRateBundleTerm', dataFromSelection.originRateBundleTerm);
    updateFormDataValue('originRateBundleTerm', dataFromSelection.originRateBundleTerm);
    setFieldValue('promotionRateBundleTerm', dataFromSelection.promotionRateBundleTerm);
    updateFormDataValue('promotionRateBundleTerm', dataFromSelection.promotionRateBundleTerm);
    setLastValidStep(getActiveStep());
  }, [getActiveStep, setFieldValue, setLastValidStep, updateFormDataValue]);

  /**
   * Find a contract bundle for a specific term value and last used contract name.
   * @param {number} termValue - The term value to search for.
   * @param {string} preferredContractName - The name of the last used contract.
   * @returns {Object|null} - The found contract bundle or null if not found.
   */
  const findBundleForTerm = useCallback((termValue, preferredContractName) => {
    const normalizedLastUsedContractName = preferredContractName.toLowerCase();

    /**
     * Check and build the data for the given bundle and related term.
     * @param {Object} bundle - The contract bundle to process.
     * @return {Object|null} - The bundle and term data or null if not found.
     */
    const buildBundleData = (bundle) => {
      // Check if promotions contract is available for this termValue
      if (bundle.promotions && bundle.promotions[termValue]) {
        return buildBundleData(bundle.promotions[termValue]);
      }

      const selectedTerm = bundle.terms.find((term) => term.termValue === termValue);
      return selectedTerm ? {
        bundle,
        term: selectedTerm,
      } : null;
    };

    /**
     * Check if a bundle matches the criteria.
     * @param {Object} bundle - The contract bundle to check.
     * @return {boolean} - True if the bundle matches, false otherwise.
     */
    const isBundleMatch = ((bundle) => {
      const isBundleNameMatch = bundle.name.toLowerCase() === normalizedLastUsedContractName;
      const isPromotionMatch = bundle.promotions?.some((promo) => promo.name.toLowerCase() === normalizedLastUsedContractName);
      return isBundleNameMatch || isPromotionMatch;
    });

    // Find term in the last used bundle
    const resultInLastUsedBundle = mergedContractsWithPromotions.find(isBundleMatch);

    if (resultInLastUsedBundle && buildBundleData(resultInLastUsedBundle)) {
      return buildBundleData(resultInLastUsedBundle);
    }

    // Fallback 1: Find term in the "Gold" bundle, if it exists
    const goldBundle = mergedContractsWithPromotions.find((bundle) => bundle.name === 'Gold');
    const resultInGoldBundle = goldBundle ? buildBundleData(goldBundle) : null;

    if (resultInGoldBundle) {
      return resultInGoldBundle;
    }

    // Fallback 2: Find term in other bundles using array methods
    const resultInOtherBundle = mergedContractsWithPromotions
      .filter((bundle) => bundle.name.toLowerCase() !== normalizedLastUsedContractName && bundle.name !== 'Gold')
      .find(isBundleMatch);

    if (resultInOtherBundle && buildBundleData(resultInOtherBundle)) {
      return buildBundleData(resultInOtherBundle);
    }

    // Otherwise, use first or return null
    return mergedContractsWithPromotions.length ? buildBundleData(mergedContractsWithPromotions[0]) : null;
  }, [mergedContractsWithPromotions]);

  /**
   * Check if a given bundle is part of the selected contract.
   * @param {Object} bundle - The bundle to check.
   * @param {Object} selectedContract - The selected contract.
   * @returns {boolean} - Whether the bundle is part of the selected contract.
   */
  const isPartOfTheSelectedContract = useCallback((bundle, selectedContract) => {
    if (bundle && selectedContract) {
      if (bundle.name.toLowerCase() === selectedContract.name.toLowerCase()) return true;
      const foundRelation = selectedContract.rateCodeDto.find((x) => (x.name.toLowerCase() === bundle.name.toLowerCase()));
      return !!foundRelation;
    }
    return false;
  }, []);

  /**
   * Handle term selection.
   * @param {number} termValue - Selected term value.
   */
  const handleTermSelection = (termValue) => {
    if (termValue === termSelection) return;
    const bundleToSelect = findBundleForTerm(termValue, contractSelection?.name);
    setTermSelection(termValue);
    if (bundleToSelect) {
      handleContractSelection(bundleToSelect);
    } else {
      // eslint-disable-next-line no-console
      console.error('No Bundle found, this could only happen when not any contract is available for selected term');
    }
  };

  /**
   * Set initial contract
   */
  useEffect(() => {
    // Initial set of contract selection
    if (mergedContractsWithPromotions && !componentInitialized.current) {
      const defaultBundleData = findBundleForTerm(termSelection, contractSelection?.name ? contractSelection.name : 'Gold');
      if (defaultBundleData?.bundle && defaultBundleData?.term) {
        const {
          contractVolumeInformation,
          priceAdjustmentRules,
        } = defaultBundleData.term;
        if (priceAdjustmentRules) {
          const newBasicAmountRule = priceAdjustmentRules.find((rule) => rule.type === 'NEW_BASIC_AMOUNT');
          setFieldValue('contractPriceAdjustmentRule', newBasicAmountRule);
        }
        componentInitialized.current = true;
        if (contractVolumeInformation) {
          setFieldValue('contractVolumeInformation', { value: contractVolumeInformation });
        }

        if (!getFormDataValue('rateBundle') || !getFormDataValue('rateBundleTerm')) {
          handleContractSelection(defaultBundleData);
        }
      }
    }
  }, [contractSelection?.name,
    findBundleForTerm,
    getFormDataValue,
    handleContractSelection,
    isLoading,
    mergedContractsWithPromotions,
    setFieldValue,
    termSelection,
  ]);

  return {
    isLoading,
    termSelection,
    contractSelection,
    getAvailableTerms,
    handleTermSelection,
    handleContractSelection,
    isPartOfTheSelectedContract,
    mergedContractsWithPromotions,
  };
}
